<template>
  <v-client-table
    ref="tableElement"
    :columns="tableOptions.columns"
    :data="batches"
    :options="tableOptions.options"
    @select="selectedBatchIds = $event.map(b => b.id)"
    @pagination="calculateTotalVisibleChildren"
    @filter="$nextTick(calculateTotalVisibleChildren)"
    @row-click="v => $emit('row-click', v)"
  >
    <template #index="{ row }">
      <div
        class="d-flex h-100 align-items-center review-batch-index"
      >
        <TableTreeExpander
          v-show="props.treeView"
          class="me-3"
          :is-expanded="!hideChildOfBatchIds.includes(row.id)"
          :isChild="!!row.parent"
          :deep="row.treeDeep"
          :total-children="row.childrenIds.length"
          :total-visible-children="totalVisibleChildren[row.id]"
          @click.stop
          @expand="hideChildOfBatchIds = hideChildOfBatchIds.filter(id => id !== row.id)"
          @collapse="hideBatchId(row.id)"
        />
        <span>{{ getBatchIndex(row.id) }}</span>
      </div>
    </template>

    <template #preview="{ row }">
      <div style="width: 50px; height: 50px">
        <PartImage :batch="row" @click.stop="" />
      </div>
    </template>

    <template #name="{ row }">
      <b class="white-space__nowrap">{{ row.part?.name }}</b>
      <div
        v-if="row.part?.type == 'SHEET'"
        class="small text-muted white-space__nowrap"
      >
        <span>
          {{ row.part?.width.toFixed(2) }}&times;
          {{ row.part?.height.toFixed(2) }}
        </span>
        <span v-if="row.part.length">
          &times; {{ row.part?.length.toFixed(2) }}
        </span>
      </div>
    </template>

    <template #stock="{ row }">
      <div v-if="row.stock">
        <b>{{ row.stock?.name }}</b>
        <div class="small text-muted">
          <span v-if="row.part?.type == 'SHEET'">
            {{ row.stock?.thickness.toFixed(2) }} mm
          </span>
          <span v-if="row.part?.type == 'TUBE'">{{ row.stock?.hash }}</span>
        </div>
      </div>
    </template>

    <template #operations="{ row }">
      <span v-for="(time, i) in row.times" :key="i">
        <CBadge color="primary">{{ time.machine?.name }}</CBadge>
      </span>
    </template>

    <template #reference="{ row }">
      {{ row.part?.reference }}
    </template>

    <template #boolean__is_valid="{ row }">
      <i
        :style="`color: ${row.is_valid === true ? 'green' : 'red'}`"
        :class="['fa', row.is_valid === true ? 'fa-check' : 'fa-times']"
      />
    </template>

    <template #price="{ row }">
      <span>
        {{
          toCurrency(
            !isNaN(row.unit_amount) && row.unit_amount != null
              ? row.unit_amount
              : 0
          )
        }}
      </span>
    </template>

    <template #total="{ row }">
      <span>
        {{
          toCurrency(
            !isNaN(row.total_amount) && row.total_amount != null
              ? row.total_amount
              : 0
          )
        }}
      </span>
    </template>

    <template #actions="{ row }">
      <div class="button-groups">
        <CButton
          v-if="row.part?.type === 'SHEET'"
          class="me-2"
          size="sm"
          color="primary"
          @click.stop="batchStore.exports(row, 'DXF')"
        >
          <b>DXF</b>
        </CButton>

        <CButton
          size="sm"
          color="primary"
          @click.stop="batchStore.exports(row, 'STP')"
        >
          <b>STP</b>
        </CButton>

        <CButton
          class="ms-2"
          size="sm"
          color="primary"
          @click.stop="batchStore.exports(row, 'CYCAD')"
        >
          <b>CyCAD</b>
        </CButton>

        <!-- PDF only available for SHEET part -->
        <CButton
          v-if="row.part?.type === 'SHEET'"
          class="ms-2"
          size="sm"
          color="primary"
          @click.stop="batchStore.exports(row, 'PDF', 'preview-pdf')"
        >
          <b>PDF</b>
        </CButton>

        <CButton
          class="ms-2"
          size="sm"
          color="warning"
          @click.stop="fileStore.download(getId(row.part?.source))"
        >
          <b>SOURCE</b>
        </CButton>
      </div>
    </template>
  </v-client-table>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, type Ref } from "vue-demi"
import { useI18n } from "vue-i18n"
import PartImage from "@/components/part-image/PartImage.vue"
import { batchStore, authStore, fileStore } from "@/store"
import { generateFilterAlgorithm, getFilterEmitsOptions, getListColumns, sleep, toCurrency } from "@/libraries/helpers"
import TableTreeExpander from '@/components/TableTreeExpander.vue'
import { treeHelpers, getBatchesSortStrings } from "../helpers"
import type { MappedInjectedBatchInterface } from '@/interfaces'
import { getId } from '@/interfaces'

const i18n = useI18n()
const emit = defineEmits(["row-click"])

const props = withDefaults(
  defineProps<{
    orderId: number
    treeView?: boolean
  }>(),
  {
    treeView: false,
  }
)

const tableElement = ref<Ref>(null)

const hideChildOfBatchIds = ref([])
const selectedBatchIds = ref([])
const totalVisibleChildren = ref({})

const batches = computed(() => batchStore.getBatchesByOrderId(props.orderId))

const tableOptions = computed(() => {
  const batchesSortStrings = getBatchesSortStrings(batches.value)

  const columns = [
    "index",
    "preview",
    "name",
    "stock",
    "operations",
    "boolean__is_valid",
    "reference",
    "quantity",
    "price",
    "total",
  ]
  if (authStore.authenticatedUser?.is_admin || authStore.authenticatedUser?.is_manufacturer) columns.push("actions")
  return {
    columns,
    options: {
      perPage: 100,
      texts: {
        count: "",
        filter: "",
        filterPlaceholder: "",
        limit: "",
        page: "",
        noResults: i18n.t("no_results"),
        loading: i18n.t("loading"),
      },
      skin: "table table-hover",
      rowAttributesCallback: row => ({ id: row.id }),
      headings: {
        index: "#",
        boolean__is_valid: i18n.t("valid"),
        name: i18n.t("name"),
        material: i18n.t("material"),
        operations: i18n.t("operations"),
        stock: i18n.t("stock"),
        quantity: i18n.t("quantity"),
        price: i18n.t("price"),
        total: i18n.t("total"),
        actions: "Downloads",
      },
      sortable: [
        "index",
        "boolean__is_valid",
        "name",
        "stock",
        "quantity",
        "reference",
        "operations",
        "price",
        "total",
      ],
      sortIcon: {
        base: "fa fa-lg",
        up: "fa-sort-asc",
        down: "fa-sort-desc",
        is: "fa-sort",
      },
      orderBy: {
        column: "index",
        ascending: false,
      },
      customSorting: {
        index: (ascending: boolean) => {
          return (a: MappedInjectedBatchInterface, b: MappedInjectedBatchInterface) => {
            let aText: string = (getBatchIndex(a.id) || "").toString().padStart(5, "0")
            let bText: string = (getBatchIndex(b.id) || "").toString().padStart(5, "0")
            if (props.treeView) {
              aText = `${batchesSortStrings[a.id]?.index || ""}${!ascending ? "~" : ""}`
              bText = `${batchesSortStrings[b.id]?.index || ""}${!ascending ? "~" : ""}`
            }
            if (ascending) return aText >= bText ? 1 : -1

            return aText <= bText ? 1 : -1
          }
        },
        boolean__is_valid: (ascending: boolean) => {
          return (a: MappedInjectedBatchInterface, b: MappedInjectedBatchInterface) => {
            let aText: string | number = +a.is_valid
            let bText: string | number = +b.is_valid
            if (props.treeView) {
              aText = batchesSortStrings[a.id]?.[`is_valid_${ascending ? "ascending" : "descending"}`] || ""
              bText = batchesSortStrings[b.id]?.[`is_valid_${ascending ? "ascending" : "descending"}`] || ""
            }
            if (ascending) return aText >= bText ? 1 : -1

            return bText >= aText ? 1 : -1
          }
        },
        name: (ascending: boolean) => {
          return (a: MappedInjectedBatchInterface, b: MappedInjectedBatchInterface) => {
            let aText = a.part.name
            let bText = b.part.name
            if (props.treeView) {
              aText = `${batchesSortStrings[a.id]?.name || ""}${!ascending ? "~" : ""}`
              bText = `${batchesSortStrings[b.id]?.name || ""}${!ascending ? "~" : ""}`
            }

            if (ascending) return aText >= bText ? 1 : -1

            return aText <= bText ? 1 : -1
          }
        },
        stock: (ascending: boolean) => {
          return (a: MappedInjectedBatchInterface, b: MappedInjectedBatchInterface) => {
            let aText = a.stock?.name || ""
            let bText = b.stock?.name || ""
            if (props.treeView) {
              aText = batchesSortStrings[a.id]?.[`stock_${ascending ? "ascending" : "descending"}`] || ""
              bText = batchesSortStrings[b.id]?.[`stock_${ascending ? "ascending" : "descending"}`] || ""
            }

            if (ascending) return aText >= bText ? 1 : -1

            return bText >= aText ? 1 : -1
          }
        },
        quantity: (ascending: boolean) => {
          return (a: MappedInjectedBatchInterface, b: MappedInjectedBatchInterface) => {
            let aText = (a.quantity || "").toString().padStart(5, "0")
            let bText = (b.quantity || "").toString().padStart(5, "0")
            if (props.treeView) {
              aText = `${batchesSortStrings[a.id]?.quantity || ""}${!ascending ? "~" : ""}`
              bText = `${batchesSortStrings[b.id]?.quantity || ""}${!ascending ? "~" : ""}`
            }
            if (ascending) return aText >= bText ? 1 : -1

            return aText <= bText ? 1 : -1
          }
        },
        reference: (ascending: boolean) => {
          return (a: MappedInjectedBatchInterface, b: MappedInjectedBatchInterface) => {
            let aText = a.part?.reference || ""
            let bText = b.part?.reference || ""
            if (props.treeView) {
              aText = batchesSortStrings[a.id]?.[`reference_${ascending ? "ascending" : "descending"}`] || ""
              bText = batchesSortStrings[b.id]?.[`reference_${ascending ? "ascending" : "descending"}`] || ""
            }
            if (ascending) return aText >= bText ? 1 : -1

            return bText >= aText ? 1 : -1
          }
        },
        operations: (ascending: boolean) => {
          return (a: MappedInjectedBatchInterface, b: MappedInjectedBatchInterface) => {
            let aText = (a.times?.length || "").toString().padStart(5, "0")
            let bText = (b.times?.length || "").toString().padStart(5, "0")
            if (props.treeView) {
              aText = `${batchesSortStrings[a.id]?.times || ""}${!ascending ? "~" : ""}`
              bText = `${batchesSortStrings[b.id]?.times || ""}${!ascending ? "~" : ""}`
            }
            if (ascending) return aText >= bText ? 1 : -1

            return aText <= bText ? 1 : -1
          }
        },
        price: (ascending: boolean) => {
          return (a: MappedInjectedBatchInterface, b: MappedInjectedBatchInterface) => {
            let aText = (a.unit_amount || "").toString().padStart(10, "0")
            let bText = (b.unit_amount || "").toString().padStart(10, "0")
            if (props.treeView) {
              aText = `${batchesSortStrings[a.id]?.unit_amount || ""}${!ascending ? "~" : ""}`
              bText = `${batchesSortStrings[b.id]?.unit_amount || ""}${!ascending ? "~" : ""}`
            }
            if (ascending) return aText >= bText ? 1 : -1

            return aText <= bText ? 1 : -1
          }
        },
        total: (ascending: boolean) => {
          return (a: MappedInjectedBatchInterface, b: MappedInjectedBatchInterface) => {
            let aText = (a.total_amount || "").toString().padStart(10, "0")
            let bText = (b.total_amount || "").toString().padStart(10, "0")
            if (props.treeView) {
              aText = `${batchesSortStrings[a.id]?.total_amount || ""}${!ascending ? "~" : ""}`
              bText = `${batchesSortStrings[b.id]?.total_amount || ""}${!ascending ? "~" : ""}`
            }
            if (ascending) return aText >= bText ? 1 : -1

            return aText <= bText ? 1 : -1
          }
        },
      },
      filterByColumn: true,
      filterable: [
        "index",
        "name",
        "stock",
        "quantity",
        "reference",
        "operations",
        "price",
        "total",
            "boolean__is_valid"
      ],
      get filterAlgorithm() {
            return {
              ...generateFilterAlgorithm(this.filterable, "boolean"),
              index: (row: MappedInjectedBatchInterface, query: string) => {
                let index = batches.value.findIndex(b => b.id === row.id)
                if (index >= 0) index++
                let str = (index || "").toString().toLowerCase()
                if (props.treeView && row.allChildrenIds.length > 0) {
                  const childrenIndexes = batches.value.filter(batch => row.allChildrenIds.includes(batch.id)).map(batch => getBatchIndex(batch.id))
                  str += `###${childrenIndexes.join("###")}`
                }
                return str.includes(query.toLowerCase())
              },
              name: (row: MappedInjectedBatchInterface, query: string) => {
                let str = (row.part?.name || "").toLowerCase()
                if (props.treeView && row.allChildrenIds.length > 0) {
                  const children = batches.value.filter(batch => row.allChildrenIds.includes(batch.id)).map(batch => (batch.part?.name || "").toLowerCase())
                  str += `###${children.join("###")}`
                }
                return str.includes(query.toLowerCase())
              },
              stock: (row: MappedInjectedBatchInterface, query: string) => {
                let str = (row.stock?.name || "").toLowerCase()
                if (props.treeView && row.allChildrenIds.length > 0) {
                  const children = batches.value.filter(batch => row.allChildrenIds.includes(batch.id)).map(batch => (batch.stock?.name || "").toLowerCase())
                  str += `###${children.join("###")}`
                }
                return str.includes(query.toLowerCase())
              },
              quantity: (row: MappedInjectedBatchInterface, query: string) => {
                let str = (row.quantity || 0).toString().toLowerCase()
                if (props.treeView && row.allChildrenIds.length > 0) {
                  const children = batches.value.filter(batch => row.allChildrenIds.includes(batch.id)).map(batch => (batch.quantity || 0).toString().toLowerCase())
                  str += `###${children.join("###")}`
                }
                return str.includes(query.toLowerCase())
              },
              operations: (row: MappedInjectedBatchInterface, query: string) => {
                let str = row.times?.map(o => o.machine?.name || "").join("###") || ""
                if (props.treeView && row.allChildrenIds.length > 0) {
                  const children = batches.value.filter(batch => row.allChildrenIds.includes(batch.id)).map(batch => batch.times?.map(o => o.machine?.name || "").join("###") || "")
                  str += `###${children.join("###")}`
                }
                return str.toLowerCase().includes(query.toLowerCase())
              },
              reference: (row: MappedInjectedBatchInterface, query: string) => {
                let str = (row.part?.reference || "").toLowerCase()
                if (props.treeView && row.allChildrenIds.length > 0) {
                  const children = batches.value.filter(batch => row.allChildrenIds.includes(batch.id)).map(batch => batch.part?.reference || "")
                  str += `###${children.join("###")}`
                }
                return str.toLowerCase().includes(query.toLowerCase())
              },
              price: (row: MappedInjectedBatchInterface, query: string) => {
                // deepcode ignore UseNumberIsNan: isNaN is more suitable than solution from Snyk (Number.isNaN)
                let str = toCurrency(isNaN(row.unit_amount) || row.unit_amount === 0 ? 0 : row.unit_amount)
                if (props.treeView && row.allChildrenIds.length > 0) {
                  // deepcode ignore UseNumberIsNan: isNaN is more suitable than solution from Snyk (Number.isNaN)
                  const children = batches.value.filter(batch => row.allChildrenIds.includes(batch.id)).map(batch => toCurrency(isNaN(batch.unit_amount) || batch.unit_amount === 0 ? 0 : batch.unit_amount))
                  str += `###${children.join("###")}`
                }
                return str.toLowerCase().includes(query.toLowerCase())
              },
              total: (row: MappedInjectedBatchInterface, query: string) => {
                // deepcode ignore UseNumberIsNan: isNaN is more suitable than solution from Snyk (Number.isNaN)
                let str = toCurrency(isNaN(row.total_amount) || row.total_amount === 0 ? 0 : row.total_amount)
                if (props.treeView && row.allChildrenIds.length > 0) {
                  // deepcode ignore UseNumberIsNan: isNaN is more suitable than solution from Snyk (Number.isNaN)
                  const children = batches.value.filter(batch => row.allChildrenIds.includes(batch.id)).map(batch => toCurrency(isNaN(batch.total_amount) || batch.total_amount === 0 ? 0 : batch.total_amount))
                  str += `###${children.join("###")}`
                }
                return str.toLowerCase().includes(query.toLowerCase())
              },
            }
          },
          get listColumns() {
            return getListColumns(this.filterable)
      },
      clientMultiSorting: false
    },
  }
})

const getBatchIndex = (id: number) => batches.value.findIndex(x => x.id == id) + 1
const calculateTotalVisibleChildren = () => {
  totalVisibleChildren.value = treeHelpers.calculateTotalVisibleChildren(batches.value, totalVisibleChildren.value)
}
const hideBatchId = (id: number) => {
  hideChildOfBatchIds.value = treeHelpers.hideBatchId(id, batches.value, hideChildOfBatchIds.value)
}

watch(batches,
  async () => {
    treeHelpers.handleBatchTree(batches.value, hideChildOfBatchIds.value)
    await sleep(100)
    calculateTotalVisibleChildren()
  },
  { deep: true }
)
watch(hideChildOfBatchIds,
  async () => {
    treeHelpers.handleBatchTree(batches.value, hideChildOfBatchIds.value)
    await sleep(100)
    calculateTotalVisibleChildren()
  },
  { deep: true }
)
watch(() => props.treeView,
  () => {
    hideChildOfBatchIds.value = []
  }
)

onMounted(() => {
  if (tableElement.value) {
    (tableElement.value as any).$.emitsOptions = {
      ...(tableElement.value as any).$.emitsOptions,
      ...getFilterEmitsOptions(tableOptions.value.options.filterable),
    }
  }
})
</script>

<style lang="scss">
.review-batch-index {
  .table-tree-expander {
    &__expanded::before {
      top: 1rem;
      height: calc(100% + 3rem + (var(--total-children) - 1) * 4.645rem);
    }
    &__children {
      &::after {
        width: 2rem;
      }
    }
  }
}
</style>
